import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import CustomDialog from '@/components/ui/Dialog/Dialog';
import Fund from '@/models/Fund';
import UploadSupportedsTab from '@/pages/Management/BulkUploads/UploadSupportedsTab';

/** Must match FeatureFlagKeys.FundBulkSupporteds on the server and SeedFeatureFlag_FundBulkSupporteds.sql. */
export const FUND_BULK_SUPPORTEDS_FEATURE_KEY = 'fund-bulk-supporteds';

/** Must match FundBulkSupportedsController.MaxRows. */
export const FUND_BULK_SUPPORTEDS_MAX_ROWS = 100;

interface FundBulkSupportedsDialogProps {
  fund: Fund;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onUploaded: () => void;
}

/**
 * The management-area supporteds upload, for the fund the user is standing in (HAT-1451). Same
 * workbook, same parser and same confirm/results steps - only the fund is fixed and the calls go
 * to the fund-scoped endpoints, which check access to this fund instead of requiring Admin.
 */
export default function FundBulkSupportedsDialog({ fund, open, setOpen, onUploaded }: FundBulkSupportedsDialogProps) {
  const { t } = useTranslation();

  return (
    <CustomDialog open={open} setOpen={setOpen} hideActionButton>
      <div dir="rtl" className="flex flex-col gap-4 min-w-[min(640px,90vw)]">
        <h2 className="heading-h3-semibold">{t('bulkUploads.fundDialogTitle')}</h2>
        <p className="body-md-regular">{t('bulkUploads.fundDialogIntro', { max: FUND_BULK_SUPPORTEDS_MAX_ROWS })}</p>
        {/* Keyed on the fund so a fund switch behind an open dialog cannot carry a half-finished
            file over to the new fund. */}
        <UploadSupportedsTab
          key={fund.id}
          targetFund={fund}
          postUrl={`funds/${fund.id}/supporteds/bulk`}
          templateUrl={`funds/${fund.id}/supporteds/bulk/template`}
          maxRows={FUND_BULK_SUPPORTEDS_MAX_ROWS}
          onUploaded={onUploaded}
        />
      </div>
    </CustomDialog>
  );
}
